import { useState, useCallback } from "react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import "@livekit/components-styles";
import { CallUI } from "../components/CallUI";
import "../styles/landing.css";

const BUILDER_EMAIL    = "omonswisdom.ict@gmail.com";
const BUILDER_WHATSAPP = "+2348141970770";
const BUILDER_NAME     = "Wisdom";

type Stage = "idle" | "connecting" | "call" | "ended";

interface Props { onDemo: () => void; }

export function LandingPage({ onDemo }: Props) {
  const [stage, setStage]   = useState<Stage>("idle");
  const [token, setToken]   = useState("");
  const [url, setUrl]       = useState("");
  const [error, setError]   = useState("");

  const connect = useCallback(async () => {
    setError(""); setStage("connecting");
    try {
      const r = await fetch("/api/token");
      const d = await r.json();
      if (!r.ok) throw new Error(d.error ?? `Error ${r.status}`);
      setToken(d.token); setUrl(d.url); setStage("call");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect.");
      setStage("idle");
    }
  }, []);

  return (
    <div className="land-root">
      {/* ── Ambient glow ── */}
      <div className="land-glow" aria-hidden />

      <div className="land-wrap">
        {/* ── Wordmark ── */}
        <header className="land-header">
          <span className="land-logo-mark">🔥</span>
          <span className="land-logo-text">HeatDesk</span>
          <span className="land-logo-tag">AI call agent</span>
        </header>

        {/* ── Hero ── */}
        <section className="land-hero">
          <div className="land-pill">Out of hours · Heating &amp; Plumbing</div>
          <h1 className="land-h1">
            Your phone answered<br />
            <span className="land-h1-accent">every night it rings</span>
          </h1>
          <p className="land-sub">
            A voice agent that books jobs, checks your service area and
            escalates real emergencies — all while you sleep.
          </p>
        </section>

        {/* ── Demo card ── */}
        <div className="land-card">
          {stage === "idle" && (
            <IdleCard onCall={connect} onDemo={onDemo} error={error} />
          )}
          {stage === "connecting" && <ConnectingCard />}
          {stage === "call" && token && url && (
            <LiveKitRoom token={token} serverUrl={url} connect audio video={false}
              onDisconnected={() => setStage("ended")} style={{ display:"contents" }}>
              <RoomAudioRenderer />
              <CallUI
                title="Heatwise Plumbing"
                subtitle="Out of hours demo · Leeds"
                onHangUp={() => setStage("ended")}
              />
            </LiveKitRoom>
          )}
          {stage === "ended" && (
            <EndedCard onAgain={() => setStage("idle")} onDemo={onDemo} />
          )}
        </div>

        {/* ── Feature strip ── */}
        <div className="land-features">
          {[
            ["⚡", "Sub-2s response",   "Feels like a real person picking up"],
            ["🛡️", "Emergency tripwire", "Gas, flooding, CO — escalated in milliseconds"],
            ["📅", "Live calendar",      "Books directly into Google Calendar"],
            ["📱", "SMS follow-ups",     "Chases no-shows so you don't have to"],
          ].map(([icon, title, body]) => (
            <div className="land-feat" key={title}>
              <span className="land-feat-icon">{icon}</span>
              <strong className="land-feat-title">{title}</strong>
              <span className="land-feat-body">{body}</span>
            </div>
          ))}
        </div>

        <footer className="land-footer">
          This call is recorded · Automated assistant · Demo uses in-memory calendar
        </footer>
      </div>
    </div>
  );
}

function IdleCard({ onCall, onDemo, error }: { onCall: () => void; onDemo: () => void; error: string }) {
  return (
    <div className="land-idle">
      <div className="land-idle-top">
        <div className="land-avatar">
          <span>🔥</span>
          <span className="land-avatar-ring" />
        </div>
        <div>
          <p className="land-idle-biz">Heatwise Plumbing</p>
          <p className="land-idle-line">Out of hours line · Leeds</p>
        </div>
      </div>
      {error && <div className="land-error">{error}</div>}
      <button className="btn-call" onClick={onCall}>
        <span className="btn-call-dot" />
        Call the demo agent
      </button>
      <div className="land-divider"><span>or</span></div>
      <button className="btn-ghost" onClick={onDemo}>
        Try it with your own business details →
      </button>
      <p className="land-mic-note">Microphone required · Chrome recommended</p>
    </div>
  );
}

function ConnectingCard() {
  return (
    <div className="land-connecting">
      <div className="connect-ring">
        <span className="connect-icon">🔥</span>
      </div>
      <p className="connect-label">Connecting to agent…</p>
    </div>
  );
}

function EndedCard({ onAgain, onDemo }: { onAgain: () => void; onDemo: () => void }) {
  const waMsg  = encodeURIComponent(`Hi ${BUILDER_NAME}, I just tried the HeatDesk demo and want to go live.`);
  const waLink = `https://wa.me/${BUILDER_WHATSAPP.replace("+", "")}?text=${waMsg}`;
  const mailLink = `mailto:${BUILDER_EMAIL}?subject=HeatDesk%20%E2%80%94%20I%20want%20to%20go%20live`;
  return (
    <div className="land-ended">
      <div className="ended-check">✓</div>
      <h3 className="ended-title">Call ended</h3>
      <p className="ended-sub">Liked what you heard? Let's put this on your real number.</p>
      <div className="ended-btns">
        <a className="btn-call" href={mailLink}>Email {BUILDER_NAME} to go live</a>
        <a className="btn-wa-land" href={waLink} target="_blank" rel="noreferrer">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{flexShrink:0}}>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp {BUILDER_NAME}
        </a>
        <button className="btn-ghost" onClick={onDemo}>Try your own business →</button>
        <button className="btn-ghost" style={{fontSize:"0.78rem"}} onClick={onAgain}>Call again</button>
      </div>
    </div>
  );
}
